import React, { useState } from "react";
import { InstanceInfo } from "../interfaces/OperatorsBalanceDashboard";
import { Box, Collapse, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, IconButton, Tooltip, Link } from "@mui/material";
import { balanceCell, monitoringIcon, noData, tableHead, tableTitle } from "./TableElements";
import { formatPriceParts } from "../payout-dashboard/PayoutDashboardHelper";
import { InstitutionAttributesMap } from "../interfaces/Institution";
import LaunchIcon from '@mui/icons-material/Launch';
import { generateSCLemonwayUrl } from "./OperatorsBalanceDashboardHelper";

export const INSTANCE_INFOS_HEADERS = ["Currency/Instance", "Balance SC", "Pending payouts", "Status"];
const SC_ALIGNMENTS: ("left" | "right" | "right" | "center")[] = ["left", "right", "right", "center"];

type OperatorDashboardSCProps = {
    scInfo: InstanceInfo[],
    attributes?: InstitutionAttributesMap | null
}

export const OperatorsBalanceDashboardSCTable = ({ scInfo, attributes }: OperatorDashboardSCProps) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Box sx={{ mt: 3 }}>
            {tableTitle('SC Monitoring', isOpen, setIsOpen)}
            <Collapse in={isOpen} timeout="auto">
                {scInfo.length === 0 ? noData() : (
                    <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'grey.300', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                        <Table size="small">
                            {tableHead(INSTANCE_INFOS_HEADERS, SC_ALIGNMENTS)}
                            <TableBody>
                                {scInfo.map((row, key) => {
                                    const { value, symbol } = formatPriceParts(row.total_to_paid, row.currency);
                                    const isApiError = row.sc_balance === -1;
                                    const isWarning = !isApiError && row.total_to_paid > row.sc_balance;
                                    
                                    const scUrl = generateSCLemonwayUrl(attributes, row.currency);

                                    return (
                                        <TableRow hover key={key} sx={isWarning ? { bgcolor: '#FFEDED' } : {}}>
                                            <TableCell align="left" sx={{ pl: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontWeight: 'bold' }}>{row.currency}</Typography>
                                                    {scUrl && (
                                                        <Tooltip title={`View Service Company ${row.currency} on Lemonway`}>
                                                            <IconButton 
                                                                component={Link} 
                                                                href={scUrl} 
                                                                target="_blank" 
                                                                size="small" 
                                                                sx={{ p: 0.5, color: 'primary.main' }}
                                                            >
                                                                <LaunchIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">{balanceCell(row.sc_balance, row.currency)}</TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', gap: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: '25px', textAlign: 'left' }}>{symbol}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">{monitoringIcon(isWarning, isApiError)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Collapse>
        </Box>
    );
}